import { useState } from "react";
import { Play, ExternalLink, WifiOff } from "lucide-react";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import { useYouTubeReachable } from "@/hooks/useYouTubeReachable";

export interface YouTubeFeatureItem {
  title: string;
  subtitle?: string;
  videoId: string;
}

interface Props {
  heading?: string;
  eyebrow?: string;
  videos: YouTubeFeatureItem[];
  className?: string;
}

/**
 * Standalone, responsive, centered YouTube section. Renders a single large
 * player at the top with a horizontal carousel of provided videos below.
 * Performs a network preflight against youtube.com and shows a friendly
 * "offline / network blocked" banner when YouTube cannot be reached at all.
 */
const YouTubeFeatureSection = ({
  heading = "Featured Videos",
  eyebrow = "Watch · YouTube",
  videos,
  className,
}: Props) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const reachable = useYouTubeReachable();
  const active = videos[activeIdx];
  if (!active) return null;

  return (
    <section
      className={`px-6 py-16 max-w-[1100px] mx-auto ${className || ""}`}
      data-testid="youtube-feature-section"
      aria-label={heading}
    >
      <div className="text-center mb-6">
        <p className="text-[10px] font-mono uppercase tracking-widest text-sovereign-gold mb-2">
          {eyebrow}
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground">{heading}</h2>
      </div>

      {reachable === false && (
        <div
          role="status"
          data-testid="youtube-preflight-banner"
          data-reachable="false"
          className="mx-auto mb-4 flex max-w-2xl items-start gap-3 rounded-lg border border-tactical-amber/40 bg-tactical-amber/10 p-3 text-left"
        >
          <WifiOff className="h-4 w-4 mt-0.5 text-tactical-amber shrink-0" />
          <div className="text-xs text-foreground">
            <p className="font-bold">YouTube appears unreachable from this network.</p>
            <p className="text-muted-foreground">
              We couldn't reach youtube.com — you may be offline, behind a firewall, or
              using an extension that blocks it. The player below may not load; use the
              "Open on YouTube" link instead.
            </p>
          </div>
        </div>
      )}

      <div className="relative mx-auto w-full overflow-hidden rounded-2xl border border-sovereign-gold/25 bg-card shadow-[0_20px_60px_-20px_hsl(var(--sovereign-gold)/0.35)]">
        <div className="relative aspect-video w-full">
          <YouTubeEmbed
            key={active.videoId}
            videoId={active.videoId}
            title={active.title}
            className="absolute inset-0 h-full w-full"
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 bg-background/70 px-4 py-3 backdrop-blur">
          <div className="min-w-0 text-left">
            {active.subtitle && (
              <p className="truncate text-[10px] font-mono uppercase tracking-widest text-sovereign-gold">
                {active.subtitle}
              </p>
            )}
            <h3 className="truncate text-sm font-bold text-foreground md:text-base">
              {active.title}
            </h3>
          </div>
          <a
            href={`https://www.youtube.com/watch?v=${active.videoId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-background/60 px-3 py-1 text-[11px] font-mono text-muted-foreground transition-colors hover:border-sovereign-gold/40 hover:text-sovereign-gold"
          >
            Open on YouTube <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      {videos.length > 1 && (
        <div
          className="mt-4 flex gap-3 overflow-x-auto pb-2 md:grid md:grid-cols-4 md:gap-3 md:overflow-visible lg:grid-cols-6"
          role="listbox"
          aria-label="Choose a video to play"
        >
          {videos.map((v, i) => {
            const isActive = i === activeIdx;
            return (
              <button
                key={v.videoId + i}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => setActiveIdx(i)}
                className={`group relative shrink-0 overflow-hidden rounded-lg border transition-all md:shrink ${
                  isActive
                    ? "border-sovereign-gold ring-2 ring-sovereign-gold/40"
                    : "border-border/60 hover:border-sovereign-gold/40"
                }`}
                style={{ width: "160px" }}
              >
                <div className="relative aspect-video w-full overflow-hidden bg-secondary">
                  <img
                    src={`https://img.youtube.com/vi/${v.videoId}/mqdefault.jpg`}
                    alt={v.title}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/85 via-background/10 to-transparent" />
                  {!isActive && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-sovereign-gold/90 shadow">
                        <Play className="ml-0.5 h-4 w-4 text-background" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-1.5 text-left">
                  <p className="truncate text-[10px] font-bold leading-tight text-foreground">
                    {v.title}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default YouTubeFeatureSection;